import axios, { isAxiosError } from 'axios';

import { URL_CONSTANTS } from '../../../constants/urlConstants';
import type {
  ClientIdName,
  TicketCreator,
  UserIdName,
  StatusObj,
  TypeObj,
  PriorityObj,
} from '../../../redux/slices/support/support.types';
import type {
  TenantUsersResponse,
  ClientsResponse,
  StandardResponse,
} from '../types/responseTypes.types';

type StandardResponseForStatus = StandardResponse<StatusObj[]>;

export async function fetchStatusesService(): Promise<StandardResponseForStatus> {
  const url = `${URL_CONSTANTS.SUPPORT.BASE_URL}${URL_CONSTANTS.SUPPORT.ENDPOINTS.GET_STATUSES}`;
  try {
    const { data } = await axios.get<StandardResponseForStatus>(url);
    return data; // this is not actual data field inside StandardResponse, instead its the the "body"
  } catch (error: unknown) {
    let message = 'Something went wrong';
    let statusCode = 500;

    if (isAxiosError<StandardResponseForStatus>(error)) {
      message = error.response?.data?.message ?? message;
      statusCode = error.response?.status ?? statusCode;
    }

    return {
      status: false,
      statusCode,
      message,
    };
  }
}

type StandardResponseForType = StandardResponse<TypeObj[]>;

export async function fetchTypesService(): Promise<StandardResponseForType> {
  const url = `${URL_CONSTANTS.SUPPORT.BASE_URL}${URL_CONSTANTS.SUPPORT.ENDPOINTS.GET_TYPES}`;
  try {
    const { data } = await axios.get<StandardResponseForType>(url);
    return data;
  } catch (error: unknown) {
    let message = 'Something went wrong';
    let statusCode = 500;

    if (isAxiosError<StandardResponseForType>(error)) {
      message = error.response?.data?.message ?? message;
      statusCode = error.response?.status ?? statusCode;
    }

    return {
      status: false,
      statusCode,
      message,
    };
  }
}

type StandardResponseForPriority = StandardResponse<PriorityObj[]>;

export async function fetchPrioritiesService(): Promise<StandardResponseForPriority> {
  const url = `${URL_CONSTANTS.SUPPORT.BASE_URL}${URL_CONSTANTS.SUPPORT.ENDPOINTS.GET_PRIORITIES}`;
  try {
    const { data } = await axios.get<StandardResponseForPriority>(url);
    return data;
  } catch (error: unknown) {
    let message = 'Something went wrong';
    let statusCode = 500;

    if (isAxiosError<StandardResponseForPriority>(error)) {
      message = error.response?.data?.message ?? message;
      statusCode = error.response?.status ?? statusCode;
    }

    return {
      status: false,
      statusCode,
      message,
    };
  }
}

export async function fetchTicketCreatorsService(): Promise<
  StandardResponse<TicketCreator[]>
> {
  const url = `${URL_CONSTANTS.SUPPORT.BASE_URL}${URL_CONSTANTS.SUPPORT.ENDPOINTS.GET_TICKET_CREATORS}`;
  try {
    const { data } = await axios.get<StandardResponse<TicketCreator[]>>(url);
    return data;
  } catch (error: unknown) {
    let message = 'Something went wrong';
    let statusCode = 500;

    if (isAxiosError<StandardResponse<TicketCreator[]>>(error)) {
      message = error.response?.data?.message ?? message;
      statusCode = error.response?.status ?? statusCode;
    }

    return {
      status: false,
      statusCode,
      message,
    };
  }
}

export async function fetchClientsService(): Promise<
  ClientsResponse<ClientIdName[]>
> {
  try {
    const { data } = await axios.get<ClientsResponse<ClientIdName[]>>(
      URL_CONSTANTS.SUPPORT.CLIENT_LIST_URL
    );
    return data;
  } catch {
    return {
      status: false,
    };
  }
}

export async function fetchTenantUsersService(): Promise<
  TenantUsersResponse<UserIdName[]>
> {
  try {
    const requestBody = {
      filters: [
        {
          type: 'SUPPORT_TICKET_ASSIGNABLE',
          value: [true],
        },
      ],
    };
    const { data } = await axios.post<TenantUsersResponse<UserIdName[]>>(
      URL_CONSTANTS.SUPPORT.TENANT_USERS_URL,
      requestBody
    );
    data.status = true;
    data.records = data.records?.map(u => ({
      user_id: u.user_id,
      user_name: u.user_name,
    }));
    return data;
  } catch {
    return {
      status: false,
    };
  }
}
